import safeStorage from '../../../utils/safeStorage';
import { clubTracts } from './clubBoundaryRole';
import { clubViewMaxBounds } from './clubConfirmView';

export function getClubCameraCacheKey(clubId) {
  return `club:${clubId}:camera`;
}

/** Read persisted club camera from safeStorage (sync, safe for map init). */
export function readClubCameraCache(clubId) {
  if (clubId == null) return null;
  try {
    const raw = safeStorage.getItem(getClubCameraCacheKey(clubId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.state || !Array.isArray(parsed.state.center)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function writeClubCameraCache(clubId, version, state) {
  if (clubId == null || !state || !Array.isArray(state.center)) return;
  try {
    safeStorage.setItem(getClubCameraCacheKey(clubId), JSON.stringify({
      version: Number(version || 0),
      state,
    }));
  } catch (_) {}
}

/** Build BaseMap mapConfig from a club camera state object. */
export function clubMapConfigFromCamera(state) {
  if (!state || !Array.isArray(state.center) || typeof state.zoom !== 'number') {
    return null;
  }
  const maxBounds = clubViewMaxBounds(state.bounds);
  return {
    center: state.center,
    zoom: state.zoom,
    bearing: state.bearing || 0,
    pitch: state.pitch || 0,
    minZoom: state.zoom,
    ...(maxBounds ? { maxBounds } : {}),
    usePersonalSavedView: false,
  };
}

/**
 * Prefer version-matched cache, then backend map_state.
 * @returns {{ source: 'cache'|'canonical'|'none', state: object|null, version: number }}
 */
export function resolveClubMapState(mapInfo = {}, cached = null) {
  const version = Number(mapInfo.viewport_version || 0);
  if (
    cached
    && Number(cached.version || 0) === version
    && cached.state
    && Array.isArray(cached.state.center)
    && typeof cached.state.zoom === 'number'
  ) {
    return { source: 'cache', state: cached.state, version };
  }
  const s = mapInfo.map_state || {};
  if (Array.isArray(s.center) && typeof s.zoom === 'number') {
    return { source: 'canonical', state: s, version };
  }
  return { source: 'none', state: null, version };
}

export function hasCanonicalClubViewport(resolved) {
  return resolved?.source === 'cache' || resolved?.source === 'canonical';
}

export function buildClubJumpToOptions(state) {
  if (!state || !Array.isArray(state.center)) return null;
  return {
    center: state.center,
    zoom: state.zoom,
    bearing: state.bearing || 0,
    pitch: state.pitch || 0,
  };
}

/**
 * Prefer backend boundary_bbox [w,s,e,n]; else compute from hunt boundary GeoJSON.
 * @returns {number[]|null}
 */
function bboxFromClubTracts(huntBoundaries) {
  const tracts = clubTracts(huntBoundaries);
  if (tracts.length === 0) return null;
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  const extend = (lng, lat) => {
    if (typeof lng !== 'number' || typeof lat !== 'number') return;
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  };
  tracts.forEach((b) => {
    const g = b?.geojson;
    if (!g) return;
    if (g.type === 'Polygon' && g.coordinates?.[0]) {
      g.coordinates[0].forEach((c) => extend(c[0], c[1]));
    } else if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
      g.coordinates.forEach((poly) => {
        try {
          poly[0].forEach((c) => extend(c[0], c[1]));
        } catch (_) {}
      });
    }
  });
  if (minLng === Infinity) return null;
  return [minLng, minLat, maxLng, maxLat];
}

export function resolveClubBoundaryBbox(mapInfo = {}, huntBoundaries = []) {
  // Live tracts win so a stale ClubMap.boundary_bbox cannot clip East after West.
  const fromTracts = bboxFromClubTracts(huntBoundaries);
  if (fromTracts) return fromTracts;
  const raw = mapInfo?.boundary_bbox;
  if (Array.isArray(raw) && raw.length === 4 && raw.every((n) => typeof n === 'number' && !Number.isNaN(n))) {
    return raw;
  }
  return null;
}

/** fitBounds options so the full club outline is visible (more padding on compact/mobile). */
export function clubBoundaryFitBoundsOptions({ compact = false } = {}) {
  return {
    // Larger padding → more zoomed out; phones need extra so the outline isn't clipped.
    padding: compact ? 96 : 80,
    maxZoom: 15,
    duration: 0,
  };
}

/** Rough center from [w,s,e,n] for map init before fitBounds (avoids default US flash). */
export function clubBoundaryCenterFromBbox(bbox) {
  if (!Array.isArray(bbox) || bbox.length !== 4) return null;
  const [w, s, e, n] = bbox;
  if ([w, s, e, n].some((v) => typeof v !== 'number' || Number.isNaN(v))) return null;
  return [(w + e) / 2, (s + n) / 2];
}
